import React, { useState, useEffect, createRef, useRef } from "react";
import { Card, Form, Row, Col, Button } from "react-bootstrap";
import BackButton from "@components/Buttons/BackButton/BackButton";
import SubmitButton from "@components/Buttons/SubmitButton/SubmitButton";
import CardHeader from "@components/CardHeader/CardHeader";
import MultipleLineField from "@components/Forms/MulitpleLineField";
import DismissibleAlert from "@components/Alerts/DismissibleAlert";
import { Editor } from '@toast-ui/react-editor';
import '@toast-ui/editor/dist/toastui-editor.css';
import API from "@API";
import useOauth from "@utils/customHooks/useOauth";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { formatUnprocessibleResponse } from "@utils/helperFunctions";
import useUnloadWarning from "@utils/customHooks/useUnloadWarning";
import useFlags from "@common/hooks/useFlags";

function CustomerSummary({ account_slug, project_id, minimizedSummary, releaseExecutiveSummaryGenerator }) {
  const [projectStatus, setProjectStatus] = useState("");
  const [executiveSummary, setExecutiveSummary] = useState("");
  const [generateSummary, setGenerateSummary] = useState("view");
  const [solutionSummary, setSolutionSummary] = useState("");
  const [ourResponsibilities, setOurResponsibilities] = useState("");
  const [customerResponsibilities, setCustomerResponsibilities] = useState("");
  const [outOfScope, setOutOfScope] = useState("");
  const [submitUrl, setSubmitUrl] = useState("");
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showExecSummaryAlert, setShowExecSummaryAlert] = useState(false);
  const [fieldLabels, setFieldLabels] = useState({
    executive_summary: "Executive Summary",
    solution_summary: "Solution Summary",
    our_responsibilities: "Our Responsibilities",
    client_responsibilities: "Customer Responsibilities",
    out_of_scope: "Out of Scope",
  });
  const [authorizationCode] = useOauth();

  const [errorMessages, setErrorMessages] = useState([]);
  const [showFailAlert, setShowFailAlert] = useState(false);

  const apiHost = process.env.REACT_APP_SCOPESTACK_API_HOST;

  const [isLoading, setIsLoading] = useState(true);
  const [isFormDirty, setFormDirty] = useState(false);

  const toolbarItems = [
    ['heading', 'bold', 'italic'],
    ['ul', 'ol', 'indent', 'outdent'],
    ['table', 'link'],
    [{ name: 'editorSize',
       text: 'Sm',
       tooltip: 'Set the height of the editor to small.',
       command: 'resizeSm',
       style: { backgroundImage: 'none' },
       className: 'toastui-editor-toolbar-icons' },
     { name: 'editorSize',
       text: 'Lg',
       tooltip: 'Set the height of the editor to large.',
       command: 'resizeLg',
       style: { backgroundImage: 'none', fontSize: "1.12em" },
       className: 'toastui-editor-toolbar-icons' }],
  ];

  const aiToolbarItems = [
    ['heading', 'bold', 'italic'],
    ['ul', 'ol', 'indent', 'outdent'],
    ['table', 'link'],
    [{ name: 'editorSize',
       text: 'Sm',
       tooltip: 'Set the height of the editor to small.',
       command: 'resizeSm',
       style: { backgroundImage: 'none' },
       className: 'toastui-editor-toolbar-icons' },
     { name: 'editorSize',
       text: 'Lg',
       tooltip: 'Set the height of the editor to large.',
       command: 'resizeLg',
       style: { backgroundImage: 'none', fontSize: "1.12em" },
       className: 'toastui-editor-toolbar-icons' }],
    [{ name: 'genSummary',
       text: null,
       tooltip: 'Generate content based on project details.',
       style: { fontSize: "0.75em" },
       command: 'summaryRequest',
       className: 'toastui-editor-toolbar-icons-writeit' }],
  ]

  const [editorRefs, setEditorRefs] = useState([]);

  const pollExecutiveSummaryRequest = async (reqId) => {
    // Repeat until we see a change in the executive summary request status
    while (true) {
      let pendingRequest = await API.Get(
        `${apiHost}/${account_slug}/v1/projects/${project_id}/executive-summary-requests/${reqId}`,
        authorizationCode
      );

      let status = pendingRequest.data.data.attributes.status;
      if (status === "complete" || status === "failed") {
        let execSummary = pendingRequest.data.data.attributes["executive-summary"];
        setExecutiveSummary(execSummary);
        editorRefs[0].current.editorInst.setMarkdown(execSummary);
        return pendingRequest.data;
      }

      await new Promise((resolve) => setTimeout(resolve, 5000));
    }
  }

  const initiateExecutiveSummaryRequest = async () => {
    setShowExecSummaryAlert(true);

    let reqId = 0;
    let resp = await API.Post(
      `${apiHost}/${account_slug}/v1/projects/${project_id}/executive-summary-requests`,
      {},
      authorizationCode
    );

    reqId = resp.data.data.id;
    let execSummary = await pollExecutiveSummaryRequest(reqId);

    setShowExecSummaryAlert(false);
    return execSummary;
  }

  const addCustomSizingCommand = () => {
    editorRefs.forEach((editor) => {
      if (editor.current) {
        editor.current.getInstance().addCommand('wysiwyg', 'resizeSm', () => {
          editor.current.editorInst.setHeight("240px");
        });
        editor.current.getInstance().addCommand('wysiwyg', 'resizeLg', () => {
          editor.current.editorInst.setHeight("720px");
        });
        editor.current.getInstance().addCommand('wysiwyg', 'summaryRequest', () => {
          initiateExecutiveSummaryRequest();
        });
      }
    })
  }

  useUnloadWarning(isFormDirty);

  useEffect(() => {
    if (authorizationCode != "") {
      API.Get(`${apiHost}/v1/accounts/${account_slug}`, authorizationCode).then(
        (response) => {
          const data = response.data.data;
          setFieldLabels(data.attributes["field-labels"]);
        }
      );

      API.Get(`${apiHost}/v1/me`, authorizationCode).then((response) => {
        const data = response.data.data.attributes.privileges;
        const userPrivileges = data.find(
          (p) => p.privilege === "projects.executive_summary"
        );

        if (userPrivileges) setGenerateSummary(userPrivileges["access-level"]);
      });

      API.Get(
        `${apiHost}/${account_slug}/v1/projects/${project_id}`,
        authorizationCode
      ).then((response) => {
        const data = response.data.data;
        if (data.attributes.status !== null)
          setProjectStatus(data.attributes.status);
        if (data.attributes["executive-summary"] !== null)
          setExecutiveSummary(data.attributes["executive-summary"]);
        if (data.attributes["solution-summary"])
          setSolutionSummary(data.attributes["solution-summary"]);
        if (data.attributes["our-responsibilities"] !== null)
          setOurResponsibilities(data.attributes["our-responsibilities"]);
        if (data.attributes["client-responsibilities"] !== null)
          setCustomerResponsibilities(
            data.attributes["client-responsibilities"]
          );
        if (data.attributes["out-of-scope"] !== null)
          setOutOfScope(data.attributes["out-of-scope"]);
        if (data.links.self !== null) setSubmitUrl(data.links.self);
        setIsLoading(false);
      });
    }
  }, [authorizationCode]);

  useEffect(() => {
    setEditorRefs((editorRefs) =>
      Array(5)
        .fill()
        .map((_, i) => editorRefs[i] || createRef()),
    );
  }, [projectStatus, generateSummary])

  useEffect(() => { addCustomSizingCommand(authorizationCode) }, [editorRefs, authorizationCode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormDirty(false);
    const data = {
      data: {
        type: "projects",
        id: project_id,
        attributes: {
          "executive-summary": executiveSummary,
          "solution-summary": solutionSummary,
          "our-responsibilities": ourResponsibilities,
          "client-responsibilities": customerResponsibilities,
          "out-of-scope": outOfScope,
        },
      },
    };

    API.Patch(submitUrl, data, authorizationCode)
      .then((response) => {
        if (response.status === 200) {
          setShowSuccessAlert(true);
          setTimeout(() => {
            setShowSuccessAlert(false);
          }, 3000);
        }
      })
      .catch((err) => {
        let msg = formatUnprocessibleResponse(err, "summary");
        if (err.response.status == 422) {
          msg += " Please check the Project Overview to set required fields."
        }
        setErrorMessages(msg);
        setShowFailAlert(true);
      });
  };

  const handleSuccessAlert = () => {
    if (showSuccessAlert === true) {
      return (
        <DismissibleAlert
          variant="info"
          onClose={() => setShowSuccessAlert(false)}
          text="Customer Summary updated"
        />
      );
    }
  };

  const handleExecutiveSummaryAlert = () => {
    if (showExecSummaryAlert === true) {
      return (
        <DismissibleAlert
          variant="info"
          text="Analyzing your project and writing an executive summary.  Depending on the complexity of your project this may take 30-60 seconds"
        />
      );
    }
  };

  const handleFailAlert = () => {
    if (showFailAlert === true) {
      return (
        <DismissibleAlert
          variant="warning"
          onClose={() => setShowFailAlert(false)}
          text={errorMessages}
        />
      );
    }
  };
  const handleFormChange = (event) => {
    setFormDirty(true);
  };

  const handleExecSummaryBlur = () => {
    setExecutiveSummary(editorRefs[0].current.editorInst.getMarkdown());
  }

  const handleSolnSummaryBlur = () => {
    setSolutionSummary(editorRefs[1].current.editorInst.getMarkdown());
  }

  const handleOurResponsibilitiesBlur = () => {
    setOurResponsibilities(editorRefs[2].current.editorInst.getMarkdown());
  }

  const handleClientResponsibilitiesBlur = () => {
    setCustomerResponsibilities(editorRefs[3].current.editorInst.getMarkdown());
  }

  const handleOutOfScopeBlur = () => {
    setOutOfScope(editorRefs[4].current.editorInst.getMarkdown());
  }

  return (
    <>
      {handleSuccessAlert()}
      {handleFailAlert()}
      <Card className="whiteBg details">
        <Card.Body>
          <Card.Header className="cardHeaderOverride">
            <div className="headerContainer">
              <div className="actionBtns">
                <h2>
                  <span className="text24">Solution Summary and Responsibilites</span>
                </h2>
              </div>
              { projectStatus === "building" && (
                <div className="actionBtns">
                  <Button
                    className="btnSeafoam"
                    type="button"
                    onClick={(e) => {
                      handleSubmit(e)
                    }}
                  >
                    Save
                  </Button>
                </div>
              )}
            </div>
          </Card.Header>
          {isLoading ? (
            <ScopeStackSpinner />
          ) : (
            <Form onChange={handleFormChange}>
              {handleExecutiveSummaryAlert()}
              <Row>
                <Col style={{ paddingLeft: '2em', paddingRight: '2em' }}>
                  <label className={'form-label'}>{fieldLabels["executive_summary"]}</label>
                  <Editor
                    initialValue={executiveSummary}
                    initialEditType='wysiwyg'
                    useCommandShortcut={true}
                    hideModeSwitch={true}
                    height={'200px'}
                    style={{ height: '200px', }}
                    ref={editorRefs[0]}
                    toolbarItems={(releaseExecutiveSummaryGenerator && (generateSummary == "create")) ? aiToolbarItems : toolbarItems}
                    onBlur={handleExecSummaryBlur}
                  />
                </Col>
              </Row>
              <Row>
                <Col style={{ paddingLeft: '2em', paddingRight: '2em' }}>
                  <label className={'form-label'} style={{ paddingTop: '12px' }}>{fieldLabels["solution_summary"]}</label>
                  <Editor
                    initialValue={solutionSummary}
                    initialEditType='wysiwyg'
                    useCommandShortcut={true}
                    hideModeSwitch={true}
                    height={'200px'}
                    style={{ height: '200px', }}
                    toolbarItems={toolbarItems}
                    ref={editorRefs[1]}
                    onBlur={handleSolnSummaryBlur}
                  />
                </Col>
              </Row>
              {!minimizedSummary && (
                <>
                  <Row>
                    <Col style={{ paddingLeft: '2em', paddingRight: '2em' }}>
                      <label className={'form-label'} style={{ paddingTop: '12px' }}>{fieldLabels["our_responsibilities"]}</label>
                      <Editor
                        initialValue={ourResponsibilities}
                        initialEditType='wysiwyg'
                        useCommandShortcut={true}
                        hideModeSwitch={true}
                        height={'200px'}
                        style={{ height: '200px', }}
                        toolbarItems={toolbarItems}
                        ref={editorRefs[2]}
                        onBlur={handleOurResponsibilitiesBlur}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col style={{ paddingLeft: '2em', paddingRight: '2em' }}>
                      <label className={'form-label'} style={{ paddingTop: '12px' }}>{fieldLabels["client_responsibilities"]}</label>
                      <Editor
                        initialValue={customerResponsibilities}
                        initialEditType='wysiwyg'
                        useCommandShortcut={true}
                        hideModeSwitch={true}
                        height={'200px'}
                        style={{ height: '200px', }}
                        toolbarItems={toolbarItems}
                        ref={editorRefs[3]}
                        onBlur={handleClientResponsibilitiesBlur}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col style={{ paddingLeft: '2em', paddingRight: '2em' }}>
                      <label className={'form-label'} style={{ paddingTop: '12px' }}>{fieldLabels["out_of_scope"]}</label>
                      <Editor
                        initialValue={outOfScope}
                        initialEditType='wysiwyg'
                        useCommandShortcut={true}
                        hideModeSwitch={true}
                        height={'200px'}
                        style={{ height: '200px', }}
                        toolbarItems={toolbarItems}
                        ref={editorRefs[4]}
                        onBlur={handleOutOfScopeBlur}
                      />
                    </Col>
                  </Row>
                </>
              )}
            </Form>
          )}
        </Card.Body>
      </Card>
    </>
  );
}

export default CustomerSummary;
